import React, { useEffect, useState } from "react";
import ContentSlider from "@qlik/qlik/src/components/ContentSlider";
import Media from "@qlik/qlik/src/components/Media";
import Cta from "@qlik/qlik/src/components/Cta";
import mapButtonType from "@qlik/qlik/src/components/Cta/mapButtonType";
import { getSpacingClassName } from "@qlik/qlik/src/lib/util/spacing";
import { renderSanitizedHtmlContent } from "@qlik/qlik/src/lib/util/helpers";
import * as styles from "./styles.module.css";

/**
 * HeroCard component
 * @param {Object} data - The data for the hero card.
 */
const HeroCard = ({ data }) => {
    // Destructure the data object
    const { cardItems = [], title, additionalConfiguration, customId } = data ?? {};
    // State variable to check if slider is initialized
    const [sliderInitialized, setSliderInitialized] = useState(false);
    // Get the class name for section padding
    const sectionSpacingClassName = getSpacingClassName(additionalConfiguration, "pt-5.5 pb-[98px] xl:pt-8 xl:pb-10");

    // Initialize slider on component mount
    useEffect(() => {
        setSliderInitialized(true);
    }, []);

    // Slider settings
    const settings = {
        dots: true,
        infinite: true,
        arrows: false,
        speed: 1000,
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        centerPadding: "10px",
        // Responsive settings for different breakpoints
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                },
            },
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                },
            },
            {
                breakpoint: 10000,
                settings: "unslick",
            },
        ],
    };

    // Calculate width based on cardItems.length
    const cardWidthClass = `cardWidth${cardItems.length}`;
    const negativePosition = cardItems.length !== 2 ? "-right-2 md:-right-2" : "";

    // Render HeroCard component
    return (
        <section className={`${sectionSpacingClassName} overflow-hidden`} id={customId}>
            <div className="container">
                <div className={`${styles.qlikCardMobile} row`}>
                    {title && (
                        <h2
                            className="mb-7.5 md:mb-15 title-3 sm:title-2 text-center"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                        />
                    )}
                    <div
                        className={`qlikCardMobile ${cardWidthClass} ${
                            !sliderInitialized ? "sliderNotInitialized" : ""
                        }`}
                    >
                        <ContentSlider settings={settings}>
                            {/* Map through card items and render each one */}
                            {cardItems?.map((card) => (
                                <div className="sliderCard flex flex-grow flex-shrink-0 h-full" key={card.id}>
                                    <Cta
                                        data={card.cta?.[0]}
                                        className="cardCta after:hidden block py-5 pr-0 pl-5 sm:pb-5 xl:py-8 xl:pr-0 xl:pl-10 group w-full overflow-hidden shadow-lgLight rounded relative hover:no-underline h-60 xl:transition-shadow xl:duration-300  xl:hover:shadow-xl"
                                    >
                                        <div className="cardTextWrapper relative z-3 h-full flex flex-col">
                                            {card?.title && (
                                                <h3
                                                    className={`cardText mb-2 md:mb-3.5 text-base md:text-lg xl:text-xl tracking-normal xl:line-clamp-2`}
                                                >
                                                    {card.title}
                                                </h3>
                                            )}
                                            {card?.description?.description && (
                                                <p className="cardText pr-7.5 sm:pr-0 mb-5.75 text-gray text-sm sm:text-md tracking-tight2 md:tracking-tight4 leading-snug lg:line-clamp-3">
                                                    {card.description?.description}
                                                </p>
                                            )}
                                            {/* Render the CTA text if it exists */}
                                            {card?.cta?.[0]?.text && (
                                                <span
                                                    data-type={card.cta?.[0]?.type}
                                                    className={`cardInnerCta ${mapButtonType(card.cta?.[0]?.type)}`}
                                                >
                                                    {card.cta?.[0].text}
                                                </span>
                                            )}
                                        </div>
                                        {/* Render the media if it exists */}
                                        {card?.media && (
                                            <>
                                                {card?.media?.[0] && (
                                                    <div className={`cardImageWrapper z-3 h-25 ${negativePosition}`}>
                                                        <Media
                                                            media={card?.media?.[0]}
                                                            className="object-cover shadow-[0px_5px_30px_0px_rgba(0,0,0,0.08)]"
                                                        />
                                                    </div>
                                                )}
                                                {card?.media?.[1] && (
                                                    <div className="absolute right-0 top-0 max-w-[200px]">
                                                        <Media
                                                            media={card?.media?.[1]}
                                                            className="w-[121px] sm:w-[150px] xl:w-auto"
                                                        />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </Cta>
                                </div>
                            ))}
                        </ContentSlider>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroCard;
